'use client';

import { ReactNode } from 'react';
import { T } from '@/lib/tokens';
import { Btn } from './Btn';

export function StepWrap({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '56px 32px 120px',
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
      }}
    >
      {children}
    </div>
  );
}

export function StepHead({
  label,
  title,
  subtitle,
}: {
  label: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span
        style={{
          fontSize: 12,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: T.terra,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <h1
        className="serif"
        style={{
          fontSize: 36,
          lineHeight: 1.2,
          color: T.espresso,
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          style={{
            fontSize: 16,
            color: T.espressoMid,
            lineHeight: 1.65,
            maxWidth: 540,
            marginTop: 4,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

const STEP_LABELS = [
  'Goal',
  'Deployment',
  'Decision',
  'Key Aspects',
  'Background',
  'Questions',
  'Refine',
  'Follow-ups',
  'Opening',
  'Closing',
  'Brief',
  'Publish',
];

export function TopBar({ step, total }: { step: number; total: number }) {
  const pct = (step / total) * 100;
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: T.white,
        borderBottom: `1px solid ${T.creamBorder}`,
      }}
    >
      <div
        style={{
          maxWidth: 760,
          margin: '0 auto',
          padding: '14px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <span
          className="serif"
          style={{
            fontSize: 18,
            color: T.terra,
            whiteSpace: 'nowrap',
            letterSpacing: '-0.01em',
          }}
        >
          AI Interviewer
        </span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              height: 4,
              background: T.creamMid,
              borderRadius: 99,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${T.terra}, ${T.terraMid})`,
                borderRadius: 99,
                transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
              }}
            />
          </div>
        </div>
        <span
          style={{
            fontSize: 13,
            color: T.espressoLight,
            whiteSpace: 'nowrap',
            minWidth: 90,
            textAlign: 'right',
          }}
        >
          {STEP_LABELS[step - 1]} · {step} of {total}
        </span>
      </div>
    </div>
  );
}

export function Nav({
  onBack,
  onNext,
  nextLabel = 'Continue',
  backLabel = 'Back',
  nextDisabled,
  extras,
}: {
  onBack?: () => void;
  onNext?: (() => void) | null;
  nextLabel?: string;
  backLabel?: string;
  nextDisabled?: boolean;
  extras?: ReactNode;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: T.white,
        borderTop: `1px solid ${T.creamBorder}`,
        zIndex: 99,
      }}
    >
      <div
        style={{
          maxWidth: 760,
          margin: '0 auto',
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          {onBack && (
            <Btn variant="secondary" onClick={onBack}>
              ← {backLabel}
            </Btn>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {extras}
          {onNext && (
            <Btn onClick={onNext} disabled={nextDisabled}>
              {nextLabel} →
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}
